import React, { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Alert, Badge, Button, Card, Modal, Spinner, Table } from 'react-bootstrap';

import { GET_VINYL_STORED_ASSETS } from './graphql';

type StoredAsset = {
  name: string;
  folder: string;
  path: string;
  url: string;
  size: number | null;
};

type RefCheck = { url: string; ok: boolean; status: number };

type RefModalState = {
  fileName: string;
  loading: boolean;
  error: string | null;
  results: RefCheck[];
};

const formatSize = (size: number | null): string => {
  if (size === null) return '—';
  if (size < 1024) return `${size} B`;
  return `${Math.ceil(size / 1024)} KB`;
};

// Collects every http(s) string found anywhere in a parsed JSON object.
const collectUrls = (node: unknown, acc: string[]) => {
  if (typeof node === 'string') {
    if (node.startsWith('https://') || node.startsWith('http://')) acc.push(node);
    return;
  }
  if (Array.isArray(node)) {
    node.forEach((child) => collectUrls(child, acc));
    return;
  }
  if (node && typeof node === 'object') {
    Object.values(node).forEach((child) => collectUrls(child, acc));
  }
};

const VinylStoredAssetsCard: React.FC<{ itemId: string }> = ({ itemId }) => {
  // Lazy: only lists the bucket when the user asks, to avoid a GCS list on every item open.
  const [loadAssets, { data, loading, error, called }] = useLazyQuery(GET_VINYL_STORED_ASSETS, {
    variables: { itemId },
    fetchPolicy: 'no-cache',
  });

  const [refModal, setRefModal] = useState<RefModalState | null>(null);

  const result = data?.vinylStoredAssets;
  const bucket: string | undefined = result?.bucket;
  const assets: StoredAsset[] = result?.files ?? [];

  const checkReferences = async (asset: StoredAsset) => {
    setRefModal({ fileName: asset.name, loading: true, error: null, results: [] });
    try {
      const res = await fetch(asset.url);
      if (!res.ok) throw new Error(`Could not load ${asset.name} (${res.status})`);
      const json = await res.json();
      const urls: string[] = [];
      collectUrls(json, urls);
      const results = await Promise.all(
        urls.map(async (url): Promise<RefCheck> => {
          try {
            const r = await fetch(url, { method: 'HEAD' });
            return { url, ok: r.ok, status: r.status };
          } catch {
            return { url, ok: false, status: 0 };
          }
        })
      );
      setRefModal({ fileName: asset.name, loading: false, error: null, results });
    } catch (e) {
      setRefModal({
        fileName: asset.name,
        loading: false,
        error: e instanceof Error ? e.message : 'Failed to read the file.',
        results: [],
      });
    }
  };

  const missingCount = refModal?.results.filter((r) => !r.ok).length ?? 0;

  return (
    <Card className="mt-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Stored assets</h5>
        <Button type="button" variant="outline-secondary" size="sm" onClick={() => loadAssets()} disabled={loading}>
          {loading ? <Spinner as="span" animation="border" size="sm" /> : called ? 'Refresh' : 'Load'}
        </Button>
      </Card.Header>
      <Card.Body>
        <Card.Text className="text-muted small mb-2">
          Folders <code>sp3/vinyls/{itemId}/</code> and <code>vinyls/{itemId}/</code>
          {bucket && (
            <>
              {' '}
              in bucket <code>{bucket}</code>
            </>
          )}
          .
        </Card.Text>

        {!called && !loading && (
          <div className="text-muted">
            Click <strong>Load</strong> to list the files stored for this vinyl.
          </div>
        )}
        {error && <Alert variant="danger">{error.message}</Alert>}
        {called && !loading && !error && assets.length === 0 && (
          <Alert variant="warning" className="mb-0">
            No files found for this vinyl yet.
          </Alert>
        )}
        {assets.length > 0 && (
          <Table striped bordered size="sm" className="mb-0">
            <thead>
              <tr>
                <th>File</th>
                <th>Full path</th>
                <th style={{ width: 80 }}>Size</th>
                <th style={{ width: 170 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((asset) => (
                <tr key={asset.path}>
                  <td>
                    <code>{asset.name}</code>
                  </td>
                  <td>
                    <code className="small">{`${bucket ?? ''}/${asset.path}`}</code>
                  </td>
                  <td>{formatSize(asset.size)}</td>
                  <td>
                    <a
                      className="btn btn-outline-secondary btn-sm mr-2"
                      href={asset.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open
                    </a>
                    {asset.name.toLowerCase().endsWith('.json') && (
                      <Button
                        type="button"
                        variant="outline-primary"
                        size="sm"
                        onClick={() => checkReferences(asset)}
                      >
                        References
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      <Modal show={refModal !== null} onHide={() => setRefModal(null)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            Assets referenced by <code>{refModal?.fileName}</code>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {refModal?.loading && (
            <div className="text-center">
              <Spinner animation="border" /> Checking references…
            </div>
          )}
          {refModal?.error && <Alert variant="danger">{refModal.error}</Alert>}
          {!refModal?.loading && !refModal?.error && (
            <>
              <p className="text-muted">
                {refModal?.results.length ?? 0} referenced asset(s)
                {missingCount > 0 ? (
                  <>
                    {' — '}
                    <Badge variant="danger">{missingCount} missing</Badge>
                  </>
                ) : (
                  <>
                    {' — '}
                    <Badge variant="success">all reachable</Badge>
                  </>
                )}
              </p>
              <Table striped bordered size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Status</th>
                    <th>URL</th>
                  </tr>
                </thead>
                <tbody>
                  {refModal?.results.map((r) => (
                    <tr key={r.url}>
                      <td>
                        {r.ok ? <Badge variant="success">OK</Badge> : <Badge variant="danger">{r.status || 'ERR'}</Badge>}
                      </td>
                      <td>
                        <a href={r.url} target="_blank" rel="noopener noreferrer" className="small">
                          {r.url}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={() => setRefModal(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default VinylStoredAssetsCard;
